import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  FormHelperText,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { VideoPlatformMeeting } from "../../api/video_platform";
import { useVideoPlatformApi } from "./hooks/useVideoPlatformApi";
import { useVideoPlatformStore } from "../../store/videoPlatformStore";

interface MeetingDetailsDialogProps {
  open: boolean;
  meeting: VideoPlatformMeeting | null;
  onClose: () => void;
}

export const MeetingDetailsDialog: React.FC<MeetingDetailsDialogProps> = ({
  open,
  meeting,
  onClose,
}) => {
  const { updateMeeting } = useVideoPlatformApi();
  const { loadMeetings: refreshMeetings } = useVideoPlatformStore();

  const [isPublicVisible, setIsPublicVisible] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  // Sync local state with meeting prop
  useEffect(() => {
    if (meeting) {
      setIsPublicVisible(meeting.isPublicVisible ?? false);
    }
  }, [meeting]);

  const handleVisibilityChange = async () => {
    if (!meeting) return;

    const previousValue = isPublicVisible;
    const newValue = !isPublicVisible;

    // Optimistic update
    setIsPublicVisible(newValue);
    setIsUpdating(true);

    try {
      await updateMeeting(meeting.externalMeetingId, {
        isPublicVisible: newValue,
      });
      await refreshMeetings();
      setSnackbar({
        open: true,
        message: newValue
          ? "Meeting is now publicly visible"
          : "Meeting is now private (Admin/Hosts only)",
        severity: "success",
      });
    } catch (error) {
      // Revert on error
      setIsPublicVisible(previousValue);
      setSnackbar({
        open: true,
        message: "Failed to update visibility",
        severity: "error",
      });
      console.error("Failed to update meeting visibility:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  if (!meeting) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Meeting Details</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Title"
                value={meeting.title}
                disabled
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Description"
                value={meeting.description || ""}
                disabled
                multiline
                rows={2}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Status"
                value={meeting.status}
                disabled
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Meeting Type"
                value={meeting.type}
                disabled
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Category"
                value={meeting.category || "-"}
                disabled
              />
            </Grid>

            {/* Visibility Settings */}
            <Grid size={{ xs: 12 }}>
              <Box
                sx={{
                  mt: 1,
                  p: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Visibility Settings
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isPublicVisible}
                      onChange={handleVisibilityChange}
                      disabled={isUpdating}
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <span>Publicly Visible</span>
                      {isUpdating && <CircularProgress size={16} />}
                    </Box>
                  }
                />
                <FormHelperText>
                  {isPublicVisible
                    ? "This meeting is visible to all users"
                    : "Only Admin and Hosts can see this meeting"}
                </FormHelperText>
              </Box>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Provider Meeting ID"
                value={meeting.externalMeetingId}
                disabled
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Created By"
                value={meeting.createdBy}
                disabled
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Host ID"
                value={meeting.hostId || ""}
                disabled
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Created At"
                value={
                  meeting.meetingCreatedAt || meeting.createdAt
                    ? new Date(
                        meeting.meetingCreatedAt || meeting.createdAt,
                      ).toLocaleString()
                    : "-"
                }
                disabled
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Started At"
                value={
                  meeting.startedAt
                    ? new Date(meeting.startedAt).toLocaleString()
                    : "-"
                }
                disabled
              />
            </Grid>

            {(meeting.endedAt || meeting.status.toUpperCase() === "ENDED") && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Ended At"
                  value={
                    meeting.endedAt
                      ? new Date(meeting.endedAt).toLocaleString()
                      : "-"
                  }
                  disabled
                />
              </Grid>
            )}

            {(meeting.endedAt || meeting.status.toUpperCase() === "ENDED") && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Duration (minutes)"
                  value={
                    meeting.duration !== undefined && meeting.duration !== null
                      ? meeting.duration
                      : meeting.durationMinutes !== undefined &&
                          meeting.durationMinutes !== null
                        ? meeting.durationMinutes
                        : "-"
                  }
                  disabled
                />
              </Grid>
            )}

            {meeting.originalMeetingId && (
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" color="text.secondary">
                  Original Meeting ID: {meeting.originalMeetingId}
                  {meeting.versionNumber &&
                    ` (Version ${meeting.versionNumber})`}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};
